import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="container max-w-4xl py-12">
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </div>
        
        <Separator className="my-6" />
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">1. Introduction</h2>
          <p>
            At WillTheyConvert ("we", "our", or "us"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy describes how we collect, use, and share information about you when you use our website, services, and applications (collectively, the "Services").
          </p>
          <p>
            This Privacy Policy complies with the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable privacy laws. It explains your rights regarding your personal data and how you can exercise those rights.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">2. Information We Collect</h2>
          
          <h3 className="text-xl font-medium">2.1 Account Information</h3>
          <p>
            When you create an account with us, we collect:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Email address</li>
            <li>Business name (if provided)</li>
            <li>Account credentials</li>
          </ul>
          
          <h3 className="text-xl font-medium">2.2 Test Analytics Data</h3>
          <p>
            When you create and run tests, we collect:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Page views and interactions</li>
            <li>Conversion events</li>
            <li>Referrer information</li>
            <li>Geographic location (country level only)</li>
            <li>Device and browser information</li>
            <li>Test completion status</li>
          </ul>
          
          <h3 className="text-xl font-medium">2.3 Test Participant Data</h3>
          <p>
            From test participants, we may collect:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Email addresses (when voluntarily provided)</li>
            <li>IP addresses (for analytics purposes only)</li>
            <li>User agent information (browser type, operating system)</li>
            <li>Country of access</li>
            <li>Interaction data with test flows</li>
          </ul>
          <p>
            Important: Our platform does not process or store any actual payment information or sensitive personal data through the simulated checkout process.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">3. How We Use Your Information</h2>
          <p>
            We use the collected information for the following purposes:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Providing and maintaining our Services</li>
            <li>Generating analytics and insights about test performance</li>
            <li>Creating conversion metrics and user behavior analysis</li>
            <li>Improving our platform's functionality</li>
            <li>Communicating with you about your account or our Services</li>
            <li>Detecting and preventing fraud</li>
            <li>Complying with legal obligations</li>
          </ul>
          
          <h3 className="text-xl font-medium">3.1 Analytics Processing</h3>
          <p>
            Our analytics processing includes:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Calculating conversion rates and engagement metrics</li>
            <li>Analyzing geographic distribution of test participants</li>
            <li>Tracking referral sources and traffic patterns</li>
            <li>Generating heatmaps and user flow visualizations</li>
            <li>Creating aggregated statistical data</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">4. Data Retention</h2>
          <p>
            We retain different types of data for different periods:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Account information: As long as your account is active</li>
            <li>Test analytics data: 90 days for individual interaction data</li>
            <li>Aggregated analytics: Retained indefinitely in anonymous form</li>
            <li>Email addresses collected through tests: Until product launch or opt-out</li>
          </ul>
          <p>
            You can request deletion of your data at any time by contacting us.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">5. Your Rights</h2>
          <p>
            You have the following rights regarding your personal data:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Request transfer of your data</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information in the Contact section.
          </p>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">6. Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal data, including:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Employee training on data protection</li>
          </ul>
        </section>
        
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">7. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="p-4 bg-muted rounded-md">
            <p>Email: <a href="mailto:hello@willtheyconvert.com" className="text-primary hover:underline">hello@willtheyconvert.com</a></p>
          </div>
        </section>
        
        <Separator className="my-6" />
        
        <div className="text-center">
          <p className="text-muted-foreground">
            <Link to="/terms" className="text-primary hover:underline">Terms of Use</Link> | <Link to="/" className="text-primary hover:underline">Home</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
